/**
 * emailDeliveryLpeFunnelApi
 *
 * DASH-EMAIL-P1-IMPL-002: client for the real, read-only Landing Page
 * Events (LPE) funnel Dashboard API
 * (`GET /api/email-delivery/dashboard/lpe-funnel`).
 *
 * Wraps the SAME shared `api` axios instance every other real-backed War
 * Room surface already uses (see lib/api.js, emailDeliveryDashboardApi.js)
 * — no second HTTP stack. Auth/tenant scoping are attached automatically
 * by that instance's own request interceptor; this module never accepts
 * or forwards a caller-supplied tenant id.
 *
 * `period` is one of the canonical named period keys
 * resolve_dashboard_reporting_period (backend) accepts -- "7d"/"30d"/
 * "mtd"/"ytd"/"today"/"yesterday"/"this_week"/"last_week"/"last_month"/
 * "this_quarter"/"last_quarter"/"last_year"/"custom" (EMAIL-DASHBOARD-
 * PERIOD-002) — the SAME named values the existing Email Dashboard Period
 * control already uses. `startDate`/`endDate` (YYYY-MM-DD) are forwarded
 * only when `period === "custom"`; the backend rejects "custom" without
 * both.
 *
 * DASH-EMAIL-P1-IMPL-032 (supersedes the prior "accepts NO Environment/
 * Program/Campaign/Broadcast/State parameter" note -- DASH-EMAIL-P1-
 * IMPL-027 added exactly those six as optional governed filters):
 * `broadcastId`/`audienceId`/`campaignId`/`programId`/`environment`/
 * `state` are resolved server-side exclusively through the derived,
 * read-only `landing_page_origin_attributions` collection -- never
 * through any raw browser claim on the canonical LPE receipt itself.
 * Values must always be the stable governed operational ID (BRC####/
 * AUD###/CMP####/PRG###) or normalized two-letter state/DC code -- never
 * a mutable display name.
 *
 * `environment` here means the governed Broadcast's OWN intended
 * environment (`resolved_environment`), a categorically different fact
 * from `emailDeliveryDashboardApi.js`'s `observedGhlEnvironment` (actual
 * GHL discovery-source provenance) -- both use the identical
 * "qualification"/"production" value vocabulary, but this client must
 * NEVER send a query parameter named `observed_ghl_environment`, and
 * `emailDeliveryDashboardApi.js` must never send one named `environment`.
 * Never conflate the two authorities, even though a single UI Environment
 * control drives both (see useEmailDashboardData.js's own contract for
 * how the shared UI value is mapped to each domain's own parameter name).
 *
 * Creative-Version remains unsupported -- no Dashboard filter for it
 * exists on either real endpoint yet.
 */
import { api } from "./api";

export async function getEmailDeliveryLpeFunnelSummary({
  period = "mtd",
  startDate,
  endDate,
  broadcastId,
  audienceId,
  campaignId,
  programId,
  environment,
  state,
} = {}) {
  const params = { period };
  if (period === "custom") {
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
  }
  if (broadcastId) params.broadcast_id = broadcastId;
  if (audienceId) params.audience_id = audienceId;
  if (campaignId) params.campaign_id = campaignId;
  if (programId) params.program_id = programId;
  if (environment && environment !== "all") params.environment = environment;
  if (state) params.state = state;
  const res = await api.get("/email-delivery/dashboard/lpe-funnel", { params });
  return res.data;
}

export default getEmailDeliveryLpeFunnelSummary;
